import {useTranslation} from "react-i18next";
import {css, Theme} from "@emotion/react";
import {calculateRemSize, flex, p, pb, pt, pv} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";

interface ISalesProgressBarProps {
    totalCount: number;
    forSaleCount: number;
}

export const SalesProgressBar = ({totalCount, forSaleCount}: ISalesProgressBarProps) => {
    const {t} = useTranslation();
    const isValidNumber = (value: number): boolean => {
        return value != null && !isNaN(value) && isFinite(value);
    };

    const hasValidData = isValidNumber(totalCount) && isValidNumber(forSaleCount);

    const calculateProgress = (): number => {
        if (!hasValidData) {
            return 0;
        }

        if (totalCount <= 0) {
            return 0;
        }

        if (forSaleCount === 0) {
            return 100;
        }

        const soldCount = totalCount - forSaleCount;

        const progress = (soldCount / totalCount) * 100;

        return Math.min(Math.max(Math.round(progress), 0), 100);
    };

    const progress = calculateProgress();
    const soldCount = hasValidData ? totalCount - forSaleCount : null;

    return (
        <>
            <div css={progressWrapper}>
                <div css={progressBar}>
                    <div css={progressFill(progress)} />
                </div>
                <div css={progressPercentage}>{hasValidData ? `${progress}%` : "-"}</div>
            </div>

            <div css={progressLabels}>
                <Text variant="info_txt_1">
                    {t("offer_box.sold_label")} {soldCount != null ? soldCount : "-"}
                </Text>
                <Text variant="info_txt_1" strong>
                    {hasValidData
                        ? forSaleCount > 0
                            ? `${t("offer_box.available_label")} ${forSaleCount}`
                            : t("offer_box.sold_out")
                        : "-"}
                </Text>
                <Text variant="info_txt_1">
                    {t("offer_box.total_label")} {hasValidData ? totalCount : "-"}
                </Text>
            </div>
        </>
    );
};

const progressWrapper = css`
    ${flex("center", "flex-start")};
    gap: ${calculateRemSize(1)};
    ${p(1, 0, 2)};
    position: relative;
`;

const progressBar = css`
    flex: 1;
    height: ${calculateRemSize(1.5)};
    background-color: rgba(215, 175, 225, 0.4);
    border-radius: ${calculateRemSize(1)};
    position: relative;
    overflow: visible;
`;

const progressFill = (percentage: number) => css`
    height: 100%;
    width: ${percentage}%;
    background-color: #d7afe1;
    border-radius: ${calculateRemSize(1)};
    transition: width 0.3s ease;
`;

const progressPercentage = css`
    font-size: ${calculateRemSize(1.5)};
    font-weight: 600;
    color: #333;
    min-width: ${calculateRemSize(4)};
    text-align: right;
`;

const progressLabels = css`
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(1)};
    color: #666;
`;
